import React from 'react';

import { cn } from '@/lib/utils';

export interface LogoIconProps {
  className?: string;
}

// LogoMark = standalone, mass-only (52×48). Use this anywhere the icon appears
// alone (collapsed sidebars, modal headers, hero auth pages, illustrations).
export const LogoMark: React.FC<LogoIconProps> = ({ className }) => (
  <svg
    viewBox="0 0 52 48"
    className={cn('h-[18px] w-auto shrink-0 text-foreground', className)}
    aria-hidden="true"
  >
    <rect x="0" y="10" width="12" height="38" rx="6" className="fill-current" />
    <rect x="20" y="0" width="12" height="48" rx="6" className="fill-current" />
    <rect x="40" y="16" width="12" height="32" rx="6" className="fill-current" />
  </svg>
);

// LogoIcon = inline-with-text variant (52×60). ONLY use this inside <Logo>;
// for standalone use, prefer <LogoMark>.
//
// Alignment is load-bearing and asymmetric ON PURPOSE. The bars are pushed
// DOWN within the 60u box (5.25u top pad, 6.75u bottom pad) so that under
// `items-center` the bar-mass optical center lands on the wordmark's x-height
// optical center — NOT on the line-box center. A lowercase wordmark with
// line-height:1 sits ~1px below its own box center, and the bars (which share
// the text baseline) otherwise float ~3px high because they extend upward from
// it. Re-measured 2026-07-01 via Range.getBoundingClientRect() on the live
// rendered "mora" text node vs the SVG's actual ink bounds (not just the
// wordmark's theoretical box center) — the prior tuning (9u/3u pad, bars
// centered at 0.556) overshot and left the icon ~1.25px LOWER than the text's
// true ink center. Bars now centered at ~0.4875 of the box height (29.25/60).
// Do NOT "re-center" these rects to y=0 — that reintroduces the 3–4px
// high-floating icon the sidebar wordmark had (worse under the iOS
// system-font fallback before Sora loads).
export const LogoIcon: React.FC<LogoIconProps> = ({ className }) => (
  <svg
    viewBox="0 0 52 60"
    className={cn('h-[20px] w-auto shrink-0 text-foreground', className)}
    aria-hidden="true"
  >
    <rect x="0" y="15.25" width="12" height="38" rx="6" className="fill-current" />
    <rect x="20" y="5.25" width="12" height="48" rx="6" className="fill-current" />
    <rect x="40" y="21.25" width="12" height="32" rx="6" className="fill-current" />
  </svg>
);

export type LogoCase = 'lowercase' | 'capital';

export interface LogoTextProps {
  className?: string;
  // Wordmark casing. Default is 'lowercase' ("mora" at 20px) — the canonical
  // brand wordmark. 'capital' ("Mora" at 18px) is an escape hatch for legacy
  // surfaces or contexts that need the older treatment; avoid in new code.
  case?: LogoCase;
}

export const LogoText: React.FC<LogoTextProps> = ({ className, case: textCase = 'lowercase' }) => {
  const isLowercase = textCase === 'lowercase';
  return (
    <span
      style={{
        fontFamily: 'Sora, ui-sans-serif, system-ui, sans-serif',
        // line-height: 1 forced inline because the leading-none Tailwind class
        // was losing the cascade to inherited 1.2, adding 4px of phantom
        // whitespace and pushing the wordmark 2px below the icon's optical
        // center. Inline wins; alignment with LogoIcon is now exact.
        lineHeight: 1,
        // Tracking tuned per case: the wide capital "M" benefits from -0.04em
        // compression; lowercase "mora" is all round counters and needs more
        // air — -0.01em keeps it tight without crowding o/a.
        letterSpacing: isLowercase ? '-0.01em' : '-0.04em',
      }}
      className={cn(
        'font-medium text-foreground',
        isLowercase ? 'text-[20px]' : 'text-[18px]',
        className,
      )}
    >
      {isLowercase ? 'mora' : 'Mora'}
    </span>
  );
};

export interface LogoProps {
  className?: string;
  iconClassName?: string;
  textClassName?: string;
  showText?: boolean;
  case?: LogoCase;
}

export const Logo: React.FC<LogoProps> = ({
  className,
  iconClassName,
  textClassName,
  showText = true,
  case: textCase = 'lowercase',
}) => {
  return (
    <div className={cn('flex items-center gap-1.5 shrink-0', className)}>
      <LogoIcon className={iconClassName} />
      {showText && <LogoText className={textClassName} case={textCase} />}
    </div>
  );
};
